import { getFilterElementsDefault, useWorkplaceState, WorkplaceBtnActions, WorkplaceBtnDelete } from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    deleteFundsTransferDetails,
    getFundsTransferDetailsExcel,
    getFundsTransferDetailsList
} from 'axios/fundsTransferDetailsApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import DataTimeText from 'components/Common/DataTime';
import Money from 'components/Common/Money';
import useApi from 'hooks/useApi';
import {
    FundsTransferDetailsFilter,
    FundsTransferDetailsTable
} from 'models/api/fundsTransferDetails';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { List } from 'reactstrap';
import './FundsTransferDetails.scss';
import Filter from './FundsTransferDetailsFilter';

const FundsTransferDetails = () => {
    const { t, i18n } = useTranslation();
    const api = useApi();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();

    return (
        <BOWorkplace<
            FundsTransferDetailsTable,
            FundsTransferDetailsTable,
            FundsTransferDetailsFilter
        >
            pageTitle="BackOfficeUploadingRegisterForTransferOfLoanAmountsToClient"
            filterParams={{
                registryId: parseInt(searchParams.get('registryId') || ''),
            }}
            initColumns={[
                {
                    key: 'id',
                    name: <Trans i18nKey="BackOfficeId" />,
                    width: 70,
                    minWidth: 70,
                },
                {
                    key: 'orderId',
                    name: <Trans i18nKey="BackOfficeAppId" />,
                },
                {
                    key: 'orderSettingBasketType',
                    name: <Trans i18nKey="BackOfficeOrderSettingBasketType" />,
                },
                {
                    key: 'clientFullName',
                    name: <Trans i18nKey="BackOfficeClient" />,
                    formatter: ({ row }) => (
                        <List className="funds-transfer-details__list">
                            <li key="0">{row.clientFullName}</li>
                            <li key="1">{row.clientEmail}</li>
                        </List>
                    ),
                },
                {
                    key: 'clientDocument',
                    name: <Trans i18nKey="BackOfficeDocuments" />,
                    formatter: ({ row }) => (
                        <List className="funds-transfer-details__list">
                            <li key="0">{row.clientDocNumber}</li>
                            <li key="1">{row.clientDocType}</li>
                        </List>
                    ),
                },
                {
                    key: 'loanAmount',
                    name: <Trans i18nKey="BackOfficeLoanAmount" />,
                    formatter: ({ row }) => <Money value={row.loanAmount} />,
                },
                {
                    key: 'bank',
                    name: <Trans i18nKey="BackOfficeBankAccount" />,
                    formatter: ({ row }) => (
                        <List className="funds-transfer-details__list">
                            <li key="0">{row.bankAccountNumber}</li>
                            <li key="1">{row.bankAccountType}</li>
                            <li key="2">{row.bankName}</li>
                        </List>
                    ),
                },
                {
                    key: 'status',
                    name: <Trans i18nKey="BackOfficeStatus" />,
                    formatter: ({ row }) => (
                        <List className="funds-transfer-details__list">
                            <li key="0">{row.statusName}</li>
                            <li key="1">
                                <DataTimeText value={row.statusDate} />
                            </li>
                        </List>
                    ),
                },
                {
                    key: 'comment',
                    name: <Trans i18nKey="BackOfficeComment" />,
                },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'base-data-grid__cell_centered no-change-modal',
                    headerCellClass: 'base-data-grid__cell_centered',
                    width: 70,
                    minWidth: 70,
                    draggable: false,
                    resizable: false,
                    clickDisable: true,
                    formatter: ({ row }) => (
                        <WorkplaceBtnActions>
                            <WorkplaceBtnDelete key={`delete-btn-${row.id}`} id={row.id} />
                        </WorkplaceBtnActions>
                    ),
                },
            ]}
            storagePath="fundsTransferDetails/columns"
            getFilterElements={getFilterElementsDefault([
                { id: 'registryId', name: t('BackOfficeLoanRegistryId') },
                { id: 'orderId', name: t('BackOfficeAppId') },
                { id: 'phone', name: t('BackOfficePhone') },
            ])}
            filter={<Filter />}
            fetchQueryFn={(filter) =>
                getFundsTransferDetailsList(api, {
                    ...filter,
                    culture: i18n.language,
                    registryId: parseInt(searchParams.get('registryId') || ''),
                })
            }
            queryKey={['fundsTransferDetails']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getFundsTransferDetailsExcel(api, filter)}
            deleteMutationFn={(id) => deleteFundsTransferDetails(api, id)}
            lineHeight={90}
            rowClass={(row) =>
                row.status === -1 ? 'funds-transfer-details__error-status-row' : undefined
            }
        />
    );
};

const useFundsTransferDetailsWorkplaceState = () =>
    useWorkplaceState<FundsTransferDetailsTable, FundsTransferDetailsTable>();

export default FundsTransferDetails;
export { useFundsTransferDetailsWorkplaceState };
